/**
 * Number-preserving JSON parser (W1-07a formats-json-rpe; contract formats/json/types.ts, judge-tech §4.5).
 *
 * `JSON.parse` loses the difference between `1` and `1.0`: Phira's integer fields reject `1.0` ("Failed to parse
 * chart JSON") and `RPEVersion: 170.0` silently becomes 160 (rpe-format.md F§0.2, F§2.2). This small strict
 * RFC 8259 parser keeps every number's exact source text (`lex`) and an `int` flag (no '.', 'e' or 'E'), records the
 * source offset of every value (`at`, UTF-16 index of the value's first character) and reports precise error
 * positions. It runs unchanged in browsers, workers and Node.
 *
 * Leniency: a leading UTF-8 BOM (U+FEFF) and leading whitespace are accepted but reported through `leading`, so the
 * importer can warn that Phira cannot load such a file (F§0.1). Everything else is strict: no comments, no trailing
 * commas, no single quotes, no leading zeros, no unescaped control characters, nothing after the root value except
 * whitespace. Duplicate object keys are kept in source order (`nodeToJson`: the last one wins, as JSON.parse does).
 *
 * Errors carry a stable `code` (JsonSyntaxErrorEx) besides the English `message`; `jsonSyntaxErrorRef` turns an
 * error into a translatable MessageRef (catalogue json.messages.ts, namespace 'json').
 *
 * Memory: a node tree is many times larger than its text, so one parse shares equal short strings and number
 * lexemes (keys, "line.png", "1.0"; small integers come from a table) and every list is allocated at its exact
 * size. Results are identical to plain slices of the text.
 */

import { setOwn } from '../../core/util/json';
import type { Json, MessageRef } from '../../core/util/types';
import type { JsonNode, JsonSyntaxError, NodeToJsonFn, ParseJsonLosslessFn } from './types';

/** Why a text is not JSON. */
export type JsonSyntaxErrorCode =
  | 'empty'
  | 'unexpectedEnd'
  | 'unterminatedString'
  | 'unexpectedChar'
  | 'expectedValue'
  | 'expectedKey'
  | 'expectedColon'
  | 'expectedCommaOrEnd'
  | 'trailingComma'
  | 'badNumber'
  | 'leadingZero'
  | 'badEscape'
  | 'badUnicodeEscape'
  | 'controlChar'
  | 'trailingData'
  | 'tooDeep';

/** JsonSyntaxError plus a stable code and the offending character (when there is one). */
export interface JsonSyntaxErrorEx extends JsonSyntaxError {
  readonly code: JsonSyntaxErrorCode;
  /** The character found at `offset` (a visible description for control characters), if any. */
  readonly found?: string;
}

/** Maximum nesting depth (RPE charts nest about 7 levels; this only protects the call stack). */
export const MAX_JSON_DEPTH = 1000;

const BOM = 0xfeff;

const C_TAB = 0x09;
const C_LF = 0x0a;
const C_CR = 0x0d;
const C_SPACE = 0x20;
const C_QUOTE = 0x22;
const C_PLUS = 0x2b;
const C_COMMA = 0x2c;
const C_MINUS = 0x2d;
const C_DOT = 0x2e;
const C_SLASH = 0x2f;
const C_0 = 0x30;
const C_1 = 0x31;
const C_9 = 0x39;
const C_COLON = 0x3a;
const C_UPPER_E = 0x45;
const C_LBRACKET = 0x5b;
const C_BACKSLASH = 0x5c;
const C_RBRACKET = 0x5d;
const C_LOWER_E = 0x65;
const C_LBRACE = 0x7b;
const C_RBRACE = 0x7d;

const ESCAPES: Readonly<Record<number, string>> = {
  [C_QUOTE]: '"',
  [C_BACKSLASH]: '\\',
  [C_SLASH]: '/',
  0x62: '\b',
  0x66: '\f',
  0x6e: '\n',
  0x72: '\r',
  0x74: '\t',
};

const MESSAGES: Readonly<Record<JsonSyntaxErrorCode, string>> = {
  empty: 'The text is empty; expected a JSON value',
  unexpectedEnd: 'The text ends too early',
  unterminatedString: 'A string is not closed with a double quote',
  unexpectedChar: 'Unexpected character',
  expectedValue: 'Expected a value (object, array, string, number, true, false or null)',
  expectedKey: 'Expected a key in double quotes',
  expectedColon: 'Expected ":" after the key',
  expectedCommaOrEnd: 'Expected "," or the end of the list or object',
  trailingComma: 'A comma is not allowed before the end of a list or object',
  badNumber: 'Malformed number',
  leadingZero: 'Numbers cannot start with 0 followed by more digits',
  badEscape: 'Unknown escape sequence in a string',
  badUnicodeEscape: 'A \\u escape needs exactly four hexadecimal digits',
  controlChar: 'Control characters must be escaped inside strings',
  trailingData: 'Unexpected text after the end of the JSON value',
  tooDeep: 'The JSON is nested too deeply',
};

/** Describes the character at `i` for messages (control characters as U+XXXX). */
function describeChar(text: string, i: number): string | undefined {
  if (i >= text.length) return undefined;
  const cp = text.codePointAt(i) ?? 0;
  if (cp < 0x20 || cp === 0x7f || cp === BOM) return `U+${cp.toString(16).toUpperCase().padStart(4, '0')}`;
  return String.fromCodePoint(cp);
}

/** 1-based line and column (in UTF-16 code units) of an offset. Lines end at "\n" (CRLF counts once). */
export function lineColAt(text: string, offset: number): { readonly line: number; readonly col: number } {
  let line = 1;
  let lineStart = 0;
  const end = Math.min(offset, text.length);
  for (let i = 0; i < end; i++) {
    if (text.charCodeAt(i) === C_LF) {
      line++;
      lineStart = i + 1;
    }
  }
  return { line, col: end - lineStart + 1 };
}

class JsonParseFailure extends Error {
  readonly code: JsonSyntaxErrorCode;
  readonly offset: number;
  constructor(code: JsonSyntaxErrorCode, offset: number) {
    super(code);
    this.code = code;
    this.offset = offset;
  }
}

function isWs(c: number): boolean {
  return c === C_SPACE || c === C_LF || c === C_CR || c === C_TAB;
}

function isDigit(c: number): boolean {
  return c >= C_0 && c <= C_9;
}

function hexValue(c: number): number {
  if (c >= C_0 && c <= C_9) return c - C_0;
  if (c >= 0x41 && c <= 0x46) return c - 0x41 + 10;
  if (c >= 0x61 && c <= 0x66) return c - 0x61 + 10;
  return -1;
}

/** Longest string / number lexeme the interner shares (keys, names, short values such as `1.0`). */
const INTERN_MAX = 24;
/** Candidates kept per interner bucket. */
const INTERN_BUCKET = 8;
/** Integer lexemes 0..SMALL_INT_MAX (and their negatives) come from a table instead of a slice of the text. */
const SMALL_INT_MAX = 999;

let smallIntLex: { readonly pos: readonly string[]; readonly neg: readonly string[] } | null = null;

function smallIntLexemes(): { readonly pos: readonly string[]; readonly neg: readonly string[] } {
  smallIntLex ??= {
    pos: Array.from({ length: SMALL_INT_MAX + 1 }, (_, n) => String(n)),
    neg: Array.from({ length: SMALL_INT_MAX + 1 }, (_, n) => `-${String(n)}`),
  };
  return smallIntLex;
}

/**
 * Shares the strings of one parse: a chart repeats the same keys ("startTime", "easingType", ...) and short values
 * ("line.png", "1.0") hundreds of thousands of times, and a fresh slice for each one costs memory and garbage
 * collection time. Lookup compares candidates in place (`startsWith` at the offset), so a hit allocates nothing.
 * The result is always exactly `text.slice(start, end)`.
 */
class Interner {
  private readonly buckets = new Map<number, string[]>();
  private readonly s: string;

  constructor(s: string) {
    this.s = s;
  }

  slice(start: number, end: number): string {
    const s = this.s;
    const len = end - start;
    if (len <= 0 || len > INTERN_MAX) return s.slice(start, end);
    // Length, first character (16 bits) and the low 12 bits of the last one, in disjoint bit ranges.
    const key = (len * 0x10000 + s.charCodeAt(start)) * 0x1000 + (s.charCodeAt(end - 1) & 0xfff);
    let bucket = this.buckets.get(key);
    if (bucket === undefined) {
      bucket = [];
      this.buckets.set(key, bucket);
    } else {
      for (const c of bucket) if (c.length === len && s.startsWith(c, start)) return c;
    }
    const out = s.slice(start, end);
    if (bucket.length < INTERN_BUCKET) bucket.push(out);
    return out;
  }
}

class Parser {
  private readonly s: string;
  private readonly n: number;
  private readonly strings: Interner;
  /**
   * Members of the containers being parsed, innermost last. Each container copies its own members out as an
   * exact-size array when it closes (`slice`), instead of growing an array of its own (growth leaves spare
   * capacity in every small list such as a beat triple).
   */
  private readonly members: unknown[] = [];
  i: number;

  constructor(s: string, start: number) {
    this.s = s;
    this.n = s.length;
    this.strings = new Interner(s);
    this.i = start;
  }

  fail(code: JsonSyntaxErrorCode, offset: number = this.i): never {
    throw new JsonParseFailure(code, offset);
  }

  skipWs(): void {
    const s = this.s;
    let i = this.i;
    while (i < this.n && isWs(s.charCodeAt(i))) i++;
    this.i = i;
  }

  value(depth: number): JsonNode {
    this.skipWs();
    if (this.i >= this.n) this.fail('unexpectedEnd');
    const c = this.s.charCodeAt(this.i);
    switch (c) {
      case C_LBRACE:
        return this.object(depth);
      case C_LBRACKET:
        return this.array(depth);
      case C_QUOTE: {
        const at = this.i;
        return { k: 'str', v: this.string(), at };
      }
      case 0x74: // t
        return this.literal('true', { k: 'bool', v: true, at: this.i });
      case 0x66: // f
        return this.literal('false', { k: 'bool', v: false, at: this.i });
      case 0x6e: // n
        return this.literal('null', { k: 'null', at: this.i });
      default:
        if (c === C_MINUS || isDigit(c)) return this.number();
        return this.fail('expectedValue');
    }
  }

  private literal(word: string, node: JsonNode): JsonNode {
    if (this.s.startsWith(word, this.i)) {
      this.i += word.length;
      return node;
    }
    // Point at the first character that differs.
    let j = 0;
    while (j < word.length && this.s.charCodeAt(this.i + j) === word.charCodeAt(j)) j++;
    return this.fail(this.i + j >= this.n ? 'unexpectedEnd' : 'unexpectedChar', this.i + j);
  }

  private object(depth: number): JsonNode {
    if (depth >= MAX_JSON_DEPTH) this.fail('tooDeep');
    const at = this.i;
    this.i++;
    this.skipWs();
    if (this.i < this.n && this.s.charCodeAt(this.i) === C_RBRACE) {
      this.i++;
      return { k: 'obj', entries: [], at };
    }
    const members = this.members;
    const base = members.length;
    for (;;) {
      this.skipWs();
      if (this.i >= this.n) this.fail('unexpectedEnd');
      const c = this.s.charCodeAt(this.i);
      if (c !== C_QUOTE) this.fail(c === C_RBRACE && members.length > base ? 'trailingComma' : 'expectedKey');
      const key = this.string();
      this.skipWs();
      if (this.i >= this.n) this.fail('unexpectedEnd');
      if (this.s.charCodeAt(this.i) !== C_COLON) this.fail('expectedColon');
      this.i++;
      const entry: readonly [string, JsonNode] = [key, this.value(depth + 1)];
      members.push(entry);
      this.skipWs();
      if (this.i >= this.n) this.fail('unexpectedEnd');
      const d = this.s.charCodeAt(this.i);
      if (d === C_COMMA) {
        this.i++;
        continue;
      }
      if (d === C_RBRACE) {
        this.i++;
        const entries = members.slice(base) as Array<readonly [string, JsonNode]>;
        members.length = base;
        return { k: 'obj', entries, at };
      }
      this.fail('expectedCommaOrEnd');
    }
  }

  private array(depth: number): JsonNode {
    if (depth >= MAX_JSON_DEPTH) this.fail('tooDeep');
    const at = this.i;
    this.i++;
    this.skipWs();
    if (this.i < this.n && this.s.charCodeAt(this.i) === C_RBRACKET) {
      this.i++;
      return { k: 'arr', items: [], at };
    }
    const members = this.members;
    const base = members.length;
    for (;;) {
      this.skipWs();
      if (this.i < this.n && this.s.charCodeAt(this.i) === C_RBRACKET && members.length > base) {
        this.fail('trailingComma');
      }
      members.push(this.value(depth + 1));
      this.skipWs();
      if (this.i >= this.n) this.fail('unexpectedEnd');
      const d = this.s.charCodeAt(this.i);
      if (d === C_COMMA) {
        this.i++;
        continue;
      }
      if (d === C_RBRACKET) {
        this.i++;
        const items = members.slice(base) as JsonNode[];
        members.length = base;
        return { k: 'arr', items, at };
      }
      this.fail('expectedCommaOrEnd');
    }
  }

  /** Parses a string token starting at the opening quote; returns its value. */
  string(): string {
    const s = this.s;
    const start = this.i;
    let i = start + 1;
    let chunk = i;
    let out = '';
    for (;;) {
      if (i >= this.n) this.fail('unterminatedString', start);
      const c = s.charCodeAt(i);
      if (c === C_QUOTE) {
        this.i = i + 1;
        // Without escapes the value is one slice of the text: shared with equal strings of this parse.
        return chunk === start + 1 ? this.strings.slice(chunk, i) : out + s.slice(chunk, i);
      }
      if (c === C_BACKSLASH) {
        out += s.slice(chunk, i);
        if (i + 1 >= this.n) this.fail('unterminatedString', start);
        const e = s.charCodeAt(i + 1);
        if (e === 0x75) {
          // \uXXXX
          let code = 0;
          for (let k = 2; k < 6; k++) {
            if (i + k >= this.n) this.fail('badUnicodeEscape', i);
            const h = hexValue(s.charCodeAt(i + k));
            if (h < 0) this.fail('badUnicodeEscape', i);
            code = code * 16 + h;
          }
          out += String.fromCharCode(code);
          i += 6;
        } else {
          const rep = ESCAPES[e];
          if (rep === undefined) this.fail('badEscape', i);
          out += rep;
          i += 2;
        }
        chunk = i;
        continue;
      }
      if (c < 0x20) this.fail('controlChar', i);
      i++;
    }
  }

  private number(): JsonNode {
    const s = this.s;
    const at = this.i;
    let i = at;
    let int = true;
    if (s.charCodeAt(i) === C_MINUS) i++;
    if (i >= this.n) this.fail('badNumber', i);
    const first = s.charCodeAt(i);
    if (first === C_0) {
      i++;
      if (i < this.n && isDigit(s.charCodeAt(i))) this.fail('leadingZero', i);
    } else if (first >= C_1 && first <= C_9) {
      while (i < this.n && isDigit(s.charCodeAt(i))) i++;
    } else {
      this.fail('badNumber', i);
    }
    if (i < this.n && s.charCodeAt(i) === C_DOT) {
      int = false;
      i++;
      if (i >= this.n || !isDigit(s.charCodeAt(i))) this.fail('badNumber', i);
      while (i < this.n && isDigit(s.charCodeAt(i))) i++;
    }
    if (i < this.n) {
      const e = s.charCodeAt(i);
      if (e === C_LOWER_E || e === C_UPPER_E) {
        int = false;
        i++;
        if (i < this.n) {
          const sign = s.charCodeAt(i);
          if (sign === C_PLUS || sign === C_MINUS) i++;
        }
        if (i >= this.n || !isDigit(s.charCodeAt(i))) this.fail('badNumber', i);
        while (i < this.n && isDigit(s.charCodeAt(i))) i++;
      }
    }
    this.i = i;
    const len = i - at;
    const neg = s.charCodeAt(at) === C_MINUS;
    if (int && len - (neg ? 1 : 0) <= 3) {
      // Small integers (beats, easing ids, colours): value from the digits, lexeme from a table.
      let n = 0;
      for (let k = neg ? at + 1 : at; k < i; k++) n = n * 10 + (s.charCodeAt(k) - C_0);
      const table = smallIntLexemes();
      return neg
        ? { k: 'num', v: n === 0 ? -0 : -n, lex: table.neg[n] ?? s.slice(at, i), int, at }
        : { k: 'num', v: n, lex: table.pos[n] ?? s.slice(at, i), int, at };
    }
    const lex = this.strings.slice(at, i);
    return { k: 'num', v: Number(lex), lex, int, at };
  }
}

function makeError(text: string, code: JsonSyntaxErrorCode, offset: number): JsonSyntaxErrorEx {
  const { line, col } = lineColAt(text, offset);
  const found = code === 'unexpectedEnd' || code === 'empty' ? undefined : describeChar(text, offset);
  const base = MESSAGES[code];
  const message = found === undefined ? base : `${base} (found "${found}")`;
  return found === undefined ? { message, offset, line, col, code } : { message, offset, line, col, code, found };
}

/**
 * Strict JSON (RFC 8259) with number lexemes. A leading BOM and leading whitespace are accepted and reported in
 * `leading`; trailing whitespace is allowed. On failure the error has the offset, 1-based line/column and a code.
 */
export const parseJsonLossless: ParseJsonLosslessFn = (text) => {
  let start = 0;
  const bom = text.length > 0 && text.charCodeAt(0) === BOM;
  if (bom) start = 1;
  let ws = start;
  while (ws < text.length && isWs(text.charCodeAt(ws))) ws++;
  const whitespace = ws > start;
  if (ws >= text.length) return { ok: false, error: makeError(text, 'empty', text.length) };
  const p = new Parser(text, ws);
  try {
    const node = p.value(0);
    p.skipWs();
    if (p.i < text.length) p.fail('trailingData');
    return { ok: true, node, leading: { bom, whitespace } };
  } catch (e) {
    if (e instanceof JsonParseFailure) return { ok: false, error: makeError(text, e.code, e.offset) };
    throw e;
  }
};

/** Plain JSON value of a node (numbers as f64; duplicate keys: the last one wins, as JSON.parse and serde_json maps). */
export const nodeToJson: NodeToJsonFn = (node) => {
  switch (node.k) {
    case 'null':
      return null;
    case 'bool':
    case 'num':
    case 'str':
      return node.v;
    case 'arr':
      return node.items.map((item) => nodeToJson(item));
    case 'obj': {
      const out: Record<string, Json> = {};
      for (const [key, value] of node.entries) setOwn(out, key, nodeToJson(value));
      return out;
    }
  }
};

/** The code of a syntax error produced by this parser ('unexpectedChar' for foreign errors). */
export function jsonSyntaxErrorCode(error: JsonSyntaxError): JsonSyntaxErrorCode {
  const code = (error as Partial<JsonSyntaxErrorEx>).code;
  return code ?? 'unexpectedChar';
}

/**
 * Translatable description of a syntax error: key `json.syntax.<code>` with params `line`, `col` and, when there
 * is one, `found` (English texts in json.messages.ts).
 */
export function jsonSyntaxErrorRef(error: JsonSyntaxError): MessageRef {
  const found = (error as Partial<JsonSyntaxErrorEx>).found;
  const params: Record<string, string | number> = { line: error.line, col: error.col };
  if (found !== undefined) params.found = found;
  return { key: `json.syntax.${jsonSyntaxErrorCode(error)}`, params };
}

/**
 * Exact source text of the string token that starts at `at` (the opening quote), quotes and escapes included,
 * e.g. for RawNumberLexeme `{ kind: 'string', lexeme: '"170"' }`. Returns null when no valid string token starts
 * there.
 */
export function stringTokenAt(text: string, at: number): string | null {
  if (text.charCodeAt(at) !== C_QUOTE) return null;
  const p = new Parser(text, at);
  try {
    p.string();
    return text.slice(at, p.i);
  } catch (e) {
    if (e instanceof JsonParseFailure) return null;
    throw e;
  }
}

/** True when a node holds an integer lexeme (no '.', 'e' or 'E'). */
export function isIntNode(node: JsonNode): node is Extract<JsonNode, { k: 'num' }> {
  return node.k === 'num' && node.int;
}

/**
 * True when serde_json reads the number as an integer: an integer lexeme other than `-0`, which serde_json parses
 * as the float -0.0 ("convert into a float ... on `-0`"), so a Rust integer field refuses it like `0.0`.
 */
export function isSerdeIntegerLexeme(node: Extract<JsonNode, { k: 'num' }>): boolean {
  return node.int && node.lex !== '-0';
}

/** True when the string holds an unpaired UTF-16 surrogate (e.g. from the escape `\uD800` without its pair). */
export function hasLoneSurrogate(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0xd800 || c > 0xdfff) continue;
    if (c <= 0xdbff) {
      const next = s.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        i++;
        continue;
      }
    }
    return true;
  }
  return false;
}

/**
 * The string with every unpaired surrogate replaced by U+FFFD (what TextEncoder writes for it, so the result
 * reads back unchanged from UTF-8).
 */
export function toWellFormedString(s: string): string {
  if (!hasLoneSurrogate(s)) return s;
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff) {
      const next = s.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        out += s[i] ?? '';
        out += s[i + 1] ?? '';
        i++;
        continue;
      }
      out += '\ufffd';
      continue;
    }
    out += c >= 0xdc00 && c <= 0xdfff ? '\ufffd' : (s[i] ?? '');
  }
  return out;
}
